import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { MassSmsSendRequest } from '#/shared/api/generated/communication/models'
import { useSendMassSmsApiV1SendMassSmsPost } from '#/shared/api/generated/communication/theCargoCommunication'
import {
  getApiErrorDetail,
  getHttpStatus,
} from '#/shared/lib/api/error-handler'
import { ErrorLogger } from '#/shared/lib/error-handling'
import {
  activeMassJobsQueryKey,
  useMassSmsPreview,
} from '#/shared/lib/mass-jobs'
import { expandTemplateChips, htmlToMultilineText } from '#/shared/lib/html'
import type { SmsDeliverability } from '#/shared/lib/sms-preview'
import { sanitizeSmsText } from '#/shared/lib/sms-sanitize'
import { computeSmsSegments } from '#/shared/lib/sms-segments'
import type { MassRemovedRecipient } from '#/shared/ui/mass-recipients'
import type { NoteEditorController } from '#/shared/ui/note-editor'
import { toast } from '#/shared/ui/sonner'

import type { MassSmsFromNumber, MassSmsViewModel } from '../lib/mass-sms'
import { useMassMedia } from './use-mass-media'
import type { MassMediaAttachment } from './use-mass-media'

export interface UseMassSmsResult {
  message: string
  setMessage: (html: string) => void
  /** The body that will actually be sent — drives the counter and cost panel. */
  plainText: string
  expandedRemoved: boolean
  toggleRemoved: () => void
  fromNumberId: string
  setFromNumberId: (value: string) => void
  editorRef: React.Ref<NoteEditorController>
  insertField: (content: string) => void
  chars: number
  segments: number
  /** Server pre-send check on the blast body: content findings plus any
   *  blast-level blocker (quota) that stops Send. */
  deliverability: SmsDeliverability
  totalSelected: number
  willReceive: number
  removed: MassRemovedRecipient[]
  removedCount: number
  totalSms: number
  sendCount: number
  hasMessage: boolean
  attachments: MassMediaAttachment[]
  addFiles: (files: FileList | null) => void
  removeAttachment: (id: string) => void
  isUploading: boolean
  attachmentsFull: boolean
  canSend: boolean
  isSending: boolean
  send: () => Promise<boolean>
}

type SendMutation = ReturnType<typeof useSendMassSmsApiV1SendMassSmsPost>

async function submitMassSms(
  mutation: SendMutation,
  queryClient: QueryClient,
  data: MassSmsSendRequest,
): Promise<void> {
  await mutation.mutateAsync({ data })
  await queryClient.invalidateQueries({ queryKey: activeMassJobsQueryKey() })
}

interface BlastShape {
  recipientCount: number
  totalSelected: number
  removedCount: number
  segments: number
  totalSms: number
  isMarketing: boolean
  hasMedia: boolean
  mediaCount: number
  fromNumberId: string
}

/**
 * Report a blast *failure* to Sentry — the shape of the send plus the error.
 * A successful send is not reported: Sentry is for things going wrong, not an
 * audit trail of healthy blasts. No message body or raw numbers.
 */
function reportBlast(shape: BlastShape, failure?: unknown): void {
  if (failure === undefined) return
  // A response without the API's detail shape used to report as "unknown" —
  // keep the raw axios/Error message and HTTP status so the report stays
  // diagnosable.
  const message =
    failure instanceof Error && failure.message.length > 0
      ? failure.message
      : 'unknown'
  ErrorLogger.logMessage('mass sms send failed', 'warning', {
    feature: 'mass-sms',
    extra: {
      ...shape,
      error: getApiErrorDetail(failure) ?? message,
      status: getHttpStatus(failure),
    },
  })
}

/**
 * Resolve the sender line: keep the user's current pick when still valid,
 * otherwise start from the global default line, then fall back to the first
 * available line.
 */
function resolveFromNumber(
  numbers: MassSmsFromNumber[],
  current: string,
  preferredId: string | undefined,
): string {
  const has = (id: string | undefined): id is string =>
    Boolean(id) && numbers.some((number) => number.id === id)
  if (has(current)) return current
  if (has(preferredId)) return preferredId
  return numbers[0]?.id ?? ''
}

/** Keeps the selected sender valid as the available numbers change, defaulting
 *  to the global default outbound line. */
function useFromNumberSelection(
  numbers: MassSmsFromNumber[],
  preferredId: string | undefined,
) {
  const [fromNumberId, setFromNumberId] = useState(() =>
    resolveFromNumber(numbers, '', preferredId),
  )
  useEffect(() => {
    setFromNumberId((current) =>
      resolveFromNumber(numbers, current, preferredId),
    )
  }, [numbers, preferredId])
  return [fromNumberId, setFromNumberId] as const
}

export function useMassSms(
  viewModel: MassSmsViewModel,
  // Externally-owned draft (the dialog hoists it per workspace tab so a tab
  // switch that unmounts the body doesn't lose the typed message).
  draft?: [string, (value: string) => void],
): UseMassSmsResult {
  const { t } = useTranslation()
  const localDraft = useState('')
  const [message, setMessage] = draft ?? localDraft
  const [expandedRemoved, setExpandedRemoved] = useState(false)
  const [fromNumberId, setFromNumberId] = useFromNumberSelection(
    viewModel.fromNumbers,
    viewModel.defaultFromNumberId,
  )
  const editorRef = useRef<NoteEditorController>(null)
  const queryClient = useQueryClient()
  const sendMutation = useSendMassSmsApiV1SendMassSmsPost()

  // Sanitized like the send path (fancy Unicode / invisible chars folded to
  // plain text) so the counter and cost math price the real wire body.
  const plainText = useMemo(
    () => sanitizeSmsText(htmlToMultilineText(expandTemplateChips(message))),
    [message],
  )
  const metrics = useMemo(() => computeSmsSegments(plainText), [plainText])
  // The body rides along with the recipient preview: the server lints it for
  // the signals that get a blast carrier-spam-blocked, multiplied here across
  // every recipient.
  const preview = useMassSmsPreview(viewModel.recipients, fromNumberId, {
    message: plainText,
    isMarketing: viewModel.isMarketing ?? false,
  })
  const media = useMassMedia()
  const willReceive = preview.recipientCount
  const hasMessage = plainText.length > 0
  const canSend =
    Boolean(fromNumberId) &&
    willReceive > 0 &&
    (hasMessage || media.hasMedia) &&
    !media.isUploading &&
    !preview.deliverability.blocked

  const send = async (): Promise<boolean> => {
    if (!canSend) return false
    const shape: BlastShape = {
      recipientCount: willReceive,
      totalSelected: preview.totalSelected,
      removedCount: preview.removedCount,
      segments: metrics.segments,
      totalSms: willReceive * metrics.segments,
      isMarketing: viewModel.isMarketing ?? false,
      hasMedia: media.hasMedia,
      mediaCount: media.attachments.length,
      fromNumberId,
    }
    try {
      await submitMassSms(sendMutation, queryClient, {
        recipients: viewModel.recipients,
        user_phone_id: fromNumberId,
        message: hasMessage ? plainText : null,
        ...(media.hasMedia && { media_urls: media.mediaUrls }),
      })
      reportBlast(shape)
      return true
    } catch (error) {
      reportBlast(shape, error)
      toast.error(getApiErrorDetail(error) ?? t('massSms.toast.error'))
      return false
    }
  }

  return {
    message,
    setMessage,
    plainText,
    expandedRemoved,
    toggleRemoved: () => setExpandedRemoved((prev) => !prev),
    fromNumberId,
    setFromNumberId,
    editorRef,
    insertField: (content) => {
      editorRef.current?.insertContent(content)
      editorRef.current?.focus()
    },
    chars: metrics.charCount,
    segments: metrics.segments,
    deliverability: preview.deliverability,
    totalSelected: preview.totalSelected,
    willReceive,
    removed: preview.removed,
    removedCount: preview.removedCount,
    totalSms: willReceive * metrics.segments,
    sendCount: hasMessage ? willReceive * metrics.segments : willReceive,
    hasMessage,
    attachments: media.attachments,
    addFiles: media.addFiles,
    removeAttachment: media.removeAttachment,
    isUploading: media.isUploading,
    attachmentsFull: media.isFull,
    canSend,
    isSending: sendMutation.isPending,
    send,
  }
}
